import { useEffect, useState, type FormEvent } from 'react'
import { PAGE_TITLES, useDocumentTitle } from '../lib/documentTitle'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Shield, Eye, EyeOff, AlertCircle, Copy, Check, KeyRound } from 'lucide-react'
import { isAxiosError } from 'axios'
import { useAuthStore } from '../stores/authStore'
import {
  analystLogin,
  forgotAnalystPassword,
  isAuthenticatorCodeRequired,
  isBusinessChoiceRequired,
  isEnrolmentRequired,
  isSignInCodeRequired,
  placeTripwire,
  totpSetup,
  totpVerifySetup,
  type TOTPSetupResponse,
} from '../lib/api'
import {
  CLIENT_DECOY_TAKEN_KEY,
  CLIENT_PLACED_DECOY_KEY,
  CLIENT_PLACE_ERROR_KEY,
  CLIENT_PLACE_HANDOFF_KEY,
  CLIENT_PLACE_INTENT_KEY,
  CLIENT_RETURN_TO_KEY,
  isTripwireLink,
  safeClientReturn,
} from '../lib/sessionKeys'

// §7 honest alerting (TASKS/fix-frontend-auth.md): this form performs REAL
// authentication against POST /auth/v2/analyst/login or fails honestly.
// The old client-side "demo mode" that minted fabricated JWTs is gone and
// must never come back — fake auth is fabrication. It also hid the fact
// that the real login route was broken underneath it.
//
// F-05: password alone never yields a session. When the backend reports
// `mfa_enrolment_required` (password OK, TOTP not enrolled) it returns a
// single-use enrolment grant instead of tokens. This page then walks the
// analyst through a one-time authenticator setup and completes the login via
// verify-setup, which burns the grant and issues the real session. The grant
// reaches ONLY the two TOTP-setup endpoints — it can read no tenant data — so
// the enrolment step is emphatically NOT a logged-in state.

//
// BRIEF-B4: the mailbox alone never gets in. When an emailed link set the
// password before an authenticator existed, the backend answers
// `sign_in_code_required` instead: the first-time step asks for the code
// texted to the mobile given at sign-up, then enrolment as above.
//
// BRIEF-B16: one person may own several businesses with one email. When the
// password is right for more than one, the backend answers
// `business_choice_required` with their names only, and this page asks
// which one before any texted code or authenticator. An authenticator code
// typed on the first step is dropped, never sent for the chosen business;
// after the choice the page asks for the chosen business's own code (texted
// or authenticator, as the backend answers) as a separate step. Every later
// request of the same sign-in carries the chosen business_id, so it runs for
// that business only. A wrong password never reaches this step.

// The backend's exact refusal when there is no mobile to text (BRIEF-B4).
const NO_PHONE_MESSAGE = 'Ask your security team to send a new sign-in invitation.'

// BRIEF-B9: Microsoft and Google sign-in give no one a session, staff
// included (410 with this sentence). Someone who still arrives here with a
// provider's redirect in the address bar (an old button or bookmark) is told
// how to sign in instead.
const PROVIDER_SIGN_IN_MESSAGE =
  'Sign in with your DigitalWatch email, password and authenticator code.'

function arrivedFromProviderSignIn(search: string): boolean {
  const params = new URLSearchParams(search)
  return params.has('state') && (params.has('code') || params.has('error'))
}

type Step =
  | 'credentials'
  | 'business_choice'
  | 'authenticator_code'
  | 'enrolment'
  | 'first_password'
  | 'texted_code'

// What one sign-in sends again on its later steps (held in memory only).
// Never an authenticator code: on the several-businesses path the code is
// asked for after the choice, for the chosen business.
type HeldLogin = { email: string; password: string; business_id?: string }

// The saved return path ProtectedRoute wrote on a signed-out bounce: this
// tab's sessionStorage first, then localStorage (exact /client only).
function storedReturnTo(): string | null {
  return sessionStorage.getItem(CLIENT_RETURN_TO_KEY) ?? localStorage.getItem(CLIENT_RETURN_TO_KEY)
}

export function LoginPage() {
  useDocumentTitle(PAGE_TITLES.signIn)
  const navigate = useNavigate()
  const location = useLocation()
  const authLogin = useAuthStore((s) => s.login)

  // A client on exact /client (rendered here directly by ProtectedRoute,
  // DW-CLIENT-PLACE-STAYS-ON-CLIENT) or carrying an exact /client return-to
  // came to set a tripwire, not to sit through a first-meeting framing (or an
  // analyst MFA detour) — every step's copy below reads off this same flag.
  // BRIEF-B11: the return-to is read with the same precedence as the routing
  // below, so a newer /client/... return (an owner on the way to the staff
  // notice) is never overridden by a leftover exact /client in localStorage.
  const isClientPlaceContext =
    isTripwireLink(location.pathname + location.search) || isTripwireLink(storedReturnTo())

  const [step, setStep] = useState<Step>('credentials')

  // Credentials step
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(() =>
    arrivedFromProviderSignIn(location.search) ? PROVIDER_SIGN_IN_MESSAGE : null,
  )
  const [isLoading, setIsLoading] = useState(false)

  // First-password step: the visitor has no password yet and asks for a
  // set-password link. The token lands in their inbox (the engine's HTML
  // form), NEVER on this screen, and the success state stays generic — the
  // backend answers 200 identically for known and unknown addresses, so this
  // page must never confirm an address, an account, or a meeting.
  const [fpEmail, setFpEmail] = useState('')
  const [fpSent, setFpSent] = useState(false)
  const [fpError, setFpError] = useState<string | null>(null)
  const [fpLoading, setFpLoading] = useState(false)

  // Texted-code step: the typed email and password are held in memory only
  // until the code goes back with them.
  const [codeLogin, setCodeLogin] = useState<HeldLogin | null>(null)
  const [textedCode, setTextedCode] = useState('')
  const [textedError, setTextedError] = useState<string | null>(null)
  const [textedLoading, setTextedLoading] = useState(false)

  // Business-choice step (BRIEF-B16): the same sign-in is held in memory
  // until a business is chosen; the names came back only after the password.
  const [choiceLogin, setChoiceLogin] = useState<HeldLogin | null>(null)
  const [businesses, setBusinesses] = useState<{ id: string; name: string }[]>([])
  const [choiceError, setChoiceError] = useState<string | null>(null)
  const [choiceLoading, setChoiceLoading] = useState(false)

  // Chosen business's authenticator step (BRIEF-B16): asked for only after
  // the choice, and sent with the chosen business_id.
  const [chosenLogin, setChosenLogin] = useState<HeldLogin | null>(null)
  const [authCode, setAuthCode] = useState('')
  const [authError, setAuthError] = useState<string | null>(null)
  const [authLoading, setAuthLoading] = useState(false)

  // Enrolment step (populated only when the backend flags enrolment)
  const [enrolmentToken, setEnrolmentToken] = useState<string | null>(null)
  const [setup, setSetup] = useState<TOTPSetupResponse | null>(null)
  const [enrolCode, setEnrolCode] = useState('')
  const [enrolError, setEnrolError] = useState<string | null>(null)
  const [enrolLoading, setEnrolLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  // True while the mount bounce is deciding whether a persisted session
  // should skip the form entirely — the password form must not flash while
  // that is in flight.
  const [bouncing, setBouncing] = useState(() => useAuthStore.getState().checkAuth())

  // Whether the current navigation is a place errand rather than an ordinary
  // sign-in: the address bar can already be sitting on exact /client
  // (DW-CLIENT-PLACE-INTENT-FROM-PATH), or router state / sessionStorage /
  // localStorage can carry an exact /client bounce from ProtectedRoute
  // (DW-CLIENT-RETURN-TO-PLACE, DW-CLIENT-PLACE-SURVIVES-REFRESH). Shared by
  // completeLogin (which needs the answer before a session even exists) and
  // routeBySession's routing.
  //
  // router state.from is gone the moment the tab is already on "/" (a
  // refresh, a later sign-in, a CDP re-open) — ProtectedRoute also persists
  // the bounced path to sessionStorage, so fall back to that when state is
  // empty. Closing the tab drops sessionStorage too, so an exact /client
  // bounce also lands in localStorage — fall back to that next, same
  // /client prefix rule.
  const resolvePlaceIntent = (): boolean => {
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)
      ?.from
    const statePath =
      typeof from?.pathname === 'string' ? from.pathname + (from.search ?? '') : undefined
    const candidatePath = statePath ?? storedReturnTo()
    return isTripwireLink(candidatePath) || isTripwireLink(location.pathname + location.search)
  }

  // BRIEF-B11: where a signed-in client was going. A /client/... subpath (the
  // staff notice, a bookmark) is returned to; anything else is /client home.
  const clientDestination = (): string => {
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)
      ?.from
    const statePath =
      typeof from?.pathname === 'string' ? from.pathname + (from.search ?? '') : undefined
    const returnTo = statePath ?? storedReturnTo()
    return safeClientReturn(returnTo) ?? '/client'
  }

  // Shared routing for an issued session: used by completeLogin (fresh token)
  // and by the mount bounce (persisted session). Route by role only —
  // CLIENT_PLACE_INTENT_KEY is armed solely by completeLogin (with the fresh
  // JWT), never by this routing, so a passive mount bounce never re-arms it.
  const routeBySession = async () => {
    const { principal } = useAuthStore.getState()
    const role = principal?.role
    // Staff (analyst or admin) land on the SOC lead console (BRIEF-F1b).
    if (role === 'admin' || role === 'analyst') {
      navigate('/console')
      return
    }
    // Client (including org admin): home is the team (/client). Customers never
    // land on /org (DW-ORG-DIES-TEAM-HOME), and they never land on the first
    // meeting either.
    //
    // DW-CLIENT-HOME-SIGNIN-INCOMPLETE-LANDS-ON-HOME: the door no longer reads
    // the interview gate at all. An incomplete meeting used to be the default
    // stop, so a client with status none/in_progress — or a status the backend
    // could not read — was pushed to /client/interview. ClientInterviewGate
    // then replace-bounced that same incomplete client back to /client, so the
    // household only ever saw a meeting page flash past, and the flash
    // unmounted HomeTripwiresCard before they could reach Download Decoy
    // (.docx). The first meeting is not a second product at the door.
    // DigitalWatch.md: canaries are in the same picture — the tripwire lives
    // on the one holding picture at /client, so that is where every signed-in
    // client lands: none, in_progress, complete, and status-read throw alike.
    //
    // What this keeps: complete still lands on /client (it always did), and a
    // place intent still lands on /client (DW-CLIENT-PLACE-BEATS-INTERVIEW —
    // a client bounced off /client by ProtectedRoute to set a tripwire is
    // returned to the place they were headed, DW-CLIENT-RETURN-TO-PLACE).
    // Both now fall out of the same single door rather than two branches.
    // /client/interview is NOT deleted — it still exists, wrapped by
    // ClientInterviewGate, for a complete client's bookmark. It is simply
    // never the door. Never start a meeting here: this routing calls no
    // interview API at all now, so nothing here can start, stall or
    // mis-route on one — and it never hops via /dashboard, which could paint
    // a console frame before the gate has ruled. Holding the SIEM line is
    // still ProtectedRoute/ClientInterviewGate's job on /client itself: an
    // incomplete client must never be painted alerts-list chrome.

    // BRIEF-B11: read where they were going BEFORE the bounce is cleared. A
    // freshly signed-up owner sent to /client/staff-notice lands back there.
    const destination = clientDestination()

    // The bounce has been consumed: the client lands where it pointed below,
    // so a stale sessionStorage/localStorage entry must not leak into the
    // next sign-in.
    sessionStorage.removeItem(CLIENT_RETURN_TO_KEY)
    localStorage.removeItem(CLIENT_RETURN_TO_KEY)

    // Exact /client (not a subpath) is a place intent: the client opened
    // /client to set a tripwire — HomeTripwiresCard opens the sheet itself so
    // they never have to hunt for the button again. completeLogin is the ONLY
    // place that arms CLIENT_PLACE_INTENT_KEY, in the same tick a fresh JWT is
    // issued — a passive mount bounce for an already-persisted session
    // (placedThisSignIn always false here) must NEVER arm it, or a page
    // refresh / re-render would have HomeTripwiresCard auto-POST another
    // one-time Link on mount.
    navigate(destination)
  }

  // Shared completion: the real session has been issued (by login OR by
  // verify-setup). role comes straight from that response's own principal —
  // never from authStore, which is not populated until authLogin's getMe()
  // resolves (or fails) below.
  //
  // DW-CLIENT-PLACE-USES-LOGIN-JWT: place the Link tripwire with accessToken
  // itself, before authLogin ever runs. authLogin calls getMe(), and getMe
  // can fail (network, 5xx, or an outright 401) — placeTripwire used to read
  // dw_token from storage afterward, so a getMe failure that wiped dw_token
  // (or simply hadn't written it yet) meant the tripwire was never placed at
  // all. Writing dw_token and placing here removes that race entirely: the
  // JWT is used directly, not re-read.
  const completeLogin = async (accessToken: string, role: string) => {
    const placeIntent = resolvePlaceIntent()

    if (placeIntent && role === 'client') {
      // DW-CLIENT-PLACE-SECOND-SIT-DOES-NOT-RE-PLACE: a decoy already left
      // this tab via Copy (DW-CLIENT-PLACE-TAKEN-ONLY-AFTER-COPY — a POST
      // success alone is not "left") — the client is signing back in on
      // /client for some other reason (a dropped session, a fresh tab
      // reload of the same tab, etc.), not to collect a second one-time
      // secret. Sign them in and route them home without placing again.
      if (sessionStorage.getItem(CLIENT_DECOY_TAKEN_KEY)) {
        // Nothing to do — CLIENT_PLACE_INTENT_KEY is never armed by routing
        // (only completeLogin's own placeTripwire attempt below arms it),
        // so there is nothing here for HomeTripwiresCard to pick up either.
      } else if (sessionStorage.getItem(CLIENT_PLACED_DECOY_KEY)) {
        // A decoy from an earlier place this tab is still sitting here
        // un-Copied — it has not left yet, but placing another one would
        // still burn a second one-time secret nobody asked for. Just make
        // sure /client paints the existing one; never overwrite it, never
        // set dw_decoy_taken (only Copy does that).
        sessionStorage.setItem(CLIENT_PLACE_HANDOFF_KEY, '1')
      } else {
        localStorage.setItem('dw_token', accessToken)
        sessionStorage.setItem(CLIENT_PLACE_INTENT_KEY, '1')
        try {
          const result = await placeTripwire('link', accessToken)
          // Consumed on success only — HomeTripwiresCard must not double-place
          // for this same sign-in. The decoy is one-time, so hand it to the
          // home card via sessionStorage rather than dropping it.
          sessionStorage.removeItem(CLIENT_PLACE_INTENT_KEY)
          sessionStorage.setItem(
            CLIENT_PLACED_DECOY_KEY,
            JSON.stringify({ decoy: result.decoy, where_to_put_it: result.where_to_put_it }),
          )
          // DW-CLIENT-PLACE-HANDOFF-SURVIVES-CONSUME: HomeTripwiresCard removes
          // CLIENT_PLACED_DECOY_KEY once it has copied the decoy into local
          // state — this sticky marker is what keeps ProtectedRoute painting
          // /client children after that consume, through a later re-render.
          sessionStorage.setItem(CLIENT_PLACE_HANDOFF_KEY, '1')
          // DW-CLIENT-PLACE-TAKEN-ONLY-AFTER-COPY: a successful POST puts the
          // decoy in front of the client — it has NOT left this tab yet. Only
          // HomeTripwiresCard's copyDecoy sets CLIENT_DECOY_TAKEN_KEY, once
          // the client actually copies it out. Do not set it here.
        } catch {
          // The JWT was already in hand (we just wrote it above), so this is a
          // real POST failure, never the old "no Bearer yet" race — flag it so
          // HomeTripwiresCard surfaces an honest error instead of silently
          // retrying into a home that looks like a successful "No tripwires in
          // place yet." dw_place_intent stays set for HomeTripwiresCard's own
          // wait-for-Bearer retry; never retry the POST here — a second
          // attempt would burn a second one-time decoy for one sign-in. No
          // decoy ever left the tab on this branch, so dw_decoy_taken stays
          // unset — Try again must still be able to place.
          sessionStorage.setItem(CLIENT_PLACE_ERROR_KEY, '1')
          // Same sticky marker as the success path — the attempt happened, so
          // the dashboard must stay mounted to show the error sheet.
          sessionStorage.setItem(CLIENT_PLACE_HANDOFF_KEY, '1')
        }
      }
    }

    await authLogin(accessToken)
    await routeBySession()
  }

  // Mount bounce (credentials step only): a client who already holds a
  // persisted session (dw-auth-storage) must not be asked for their password
  // again — re-asking the password is how the first meeting never starts.
  // If the token exists but the principal is unknown, restore first; if the
  // restore clears the session, the unknown stays unknown and the form
  // paints. Routing is the same read-only path as completeLogin.
  useEffect(() => {
    if (!useAuthStore.getState().checkAuth()) return
    let cancelled = false
    const bounce = async () => {
      if (!useAuthStore.getState().principal) {
        await useAuthStore.getState().restoreSession()
      }
      if (cancelled) return
      if (!useAuthStore.getState().principal) {
        setBouncing(false)
        return
      }
      await routeBySession()
    }
    void bounce()
    return () => {
      cancelled = true
    }
    // Mount-only by design: the bounce must never re-fire on later state
    // changes (the first_password and enrolment steps are never bounced).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The grant is dead (expired past its 15-minute TTL, or already burned):
  // wipe the enrolment state and send the analyst back to the password step
  // with an honest message. Never pretend the setup can continue.
  const expireBackToCredentials = () => {
    setStep('credentials')
    setEnrolmentToken(null)
    setSetup(null)
    setEnrolCode('')
    setEnrolError(null)
    setTotpCode('')
    setError('Your setup session expired. Please sign in again.')
  }

  const openFirstPassword = () => {
    setStep('first_password')
    setFpSent(false)
    setFpError(null)
  }

  const backToCredentials = () => {
    setStep('credentials')
    setFpSent(false)
    setFpError(null)
  }

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault()
    setFpError(null)
    setFpLoading(true)
    try {
      await forgotAnalystPassword(fpEmail)
      // A 200 means only "request accepted" — the backend answers identically
      // whether or not the address is known, so the success state is generic.
      setFpSent(true)
    } catch {
      // ONE honest failure for every error shape. Distinct messages per
      // status could leak whether an address is known, so we never branch.
      setFpError('We could not send that request. Please check your connection and try again.')
    } finally {
      setFpLoading(false)
    }
  }

  // A painted field (password-manager autofill) never fires React's
  // onChange, so email/password/totpCode can still be "" while the DOM
  // input the client is looking at already holds their credentials. Any
  // credentials-step setState that re-renders this form — the password eye
  // is the one today — must snapshot the painted DOM values into React
  // state FIRST, or the controlled value={...} on the next render reapplies
  // the empty state and wipes what the browser painted (some browsers also
  // clear a password input outright when its type flips from password to
  // text). A mid-type state is still truth: never overwrite a non-empty
  // React state with an empty DOM read.
  // Safari / Chrome / iCloud Keychain autofill routinely paints the visible
  // input.value directly without ever committing it into FormData (that only
  // happens on an input/change event the password manager doesn't fire) —
  // FormData.get can read "" while the field on screen is full. Named
  // .value is the fallback painted source once FormData comes back empty;
  // FormData stays checked first so a scripted/synthetic submit that only
  // constructs FormData (no DOM value) keeps working unchanged.
  const readPaintedValue = (form: HTMLFormElement, name: string): string => {
    const el = form.elements.namedItem(name)
    return el instanceof HTMLInputElement ? el.value : ''
  }

  const hydratePaintedCredentials = (form: HTMLFormElement) => {
    const data = new FormData(form)
    const paintedEmail = String(data.get('email') || readPaintedValue(form, 'email')).trim()
    const paintedPassword = String(data.get('password') || readPaintedValue(form, 'password'))
    const paintedTotp = String(
      data.get('one-time-code') || readPaintedValue(form, 'one-time-code'),
    ).replace(/\D/g, '')
    if (paintedEmail) setEmail(paintedEmail)
    if (paintedPassword) setPassword(paintedPassword)
    if (paintedTotp) setTotpCode(paintedTotp)
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Snapshot painted DOM values into React state BEFORE any setState below
    // (setError, setIsLoading) re-renders this form. A painted field never
    // fired React's onChange, so email/password/totpCode can still be "" —
    // once a re-render happens, the controlled value={...} reapplies that
    // empty state and WIPES what the browser painted, mid-flight, from the
    // screen the client is looking at. Snapshotting first makes the painted
    // value survive a failed place (401/422) so a retry has something to
    // resubmit instead of blank inputs.
    hydratePaintedCredentials(e.currentTarget)
    setError(null)

    // A painted field (Safari / Chrome / iCloud Keychain autofill) is the
    // source of truth: the browser fills the DOM input directly and does
    // NOT fire React's onChange, so useState can still read "" while the
    // input the client is looking at already holds their credentials, AND
    // FormData can still read "" because autofill never fired the
    // input/change event that would have committed it. Read FormData off
    // the submitting form first, fall back to the named input's own .value
    // when that FormData entry is empty (the painted-but-uncommitted case),
    // then fall back to React state only when both are empty (e.g. a fully
    // scripted/synthetic submit in tests that never touched the DOM).
    const data = new FormData(e.currentTarget)
    const formEmail = String(
      data.get('email') || readPaintedValue(e.currentTarget, 'email') || email,
    ).trim()
    const formPassword = String(
      data.get('password') || readPaintedValue(e.currentTarget, 'password') || password,
    )
    const formTotp = String(
      data.get('one-time-code') || readPaintedValue(e.currentTarget, 'one-time-code') || totpCode,
    ).replace(/\D/g, '')

    // The code is OPTIONAL on the ordinary door (an unenrolled analyst has
    // none), but the /client place door is enrolled-client-only — the
    // Bearer that places the tripwire cannot exist without it, so the field
    // is mandatory there. Either way, if a code is present it must be
    // exactly 6 digits — catch it here so they get a clear message instead
    // of a 422 from the server.
    if (isClientPlaceContext && !/^\d{6}$/.test(formTotp)) {
      setError('Enter the 6-digit code from your authenticator app to place the tripwire.')
      return
    }
    if (formTotp && !/^\d{6}$/.test(formTotp)) {
      setError('The authenticator code must be exactly 6 digits.')
      return
    }

    // The place door is noValidate (DW-CLIENT-PLACE-NATIVE-EMAIL-MUST-NOT-BLOCK)
    // so native required/type="email" no longer stops an empty or unpainted
    // field from reaching here — re-check by hand with the same honest place
    // error the backend's 401 already uses, rather than letting an empty
    // credential reach analystLogin as a confusing 422.
    if (isClientPlaceContext && (!formEmail || !formPassword)) {
      setError(
        'That did not place the tripwire. Check the email, password, and authenticator code, then try again.',
      )
      return
    }

    setIsLoading(true)
    try {
      const result = await analystLogin({
        email: formEmail,
        password: formPassword,
        totp_code: formTotp || undefined,
      })

      if (isBusinessChoiceRequired(result)) {
        // The choice comes before the authenticator: drop any code typed
        // here, it may belong to another business.
        setChoiceLogin({ email: formEmail, password: formPassword })
        setTotpCode('')
        setBusinesses(result.businesses)
        setChoiceError(null)
        setStep('business_choice')
      } else if (isSignInCodeRequired(result)) {
        setCodeLogin({ email: formEmail, password: formPassword })
        setTextedCode('')
        setTextedError(null)
        setStep('texted_code')
      } else if (isEnrolmentRequired(result)) {
        await startEnrolment(result.enrolment_token)
      } else if (isAuthenticatorCodeRequired(result)) {
        // Only ever answered after a business choice; never expected here.
        setError('Sign-in failed. Please try again.')
      } else {
        await completeLogin(result.access_token, result.principal.role)
      }
    } catch (err) {
      // A 403 while fetching the setup material means the grant died between
      // login and setup — treat it as an expired session, not a hard error.
      if (isAxiosError(err) && err.response?.data?.detail === NO_PHONE_MESSAGE) {
        setError(NO_PHONE_MESSAGE)
      } else if (isAxiosError(err) && err.response?.status === 403) {
        expireBackToCredentials()
      } else if (isAxiosError(err) && err.response?.status === 401) {
        // Customer-facing copy: no "credentials"/server/backend/API talk on the
        // public door — just tell them what to re-check. The place-context
        // door is a tripwire errand, not a sign-in, so it must not mention
        // "sign-in" or an authenticator code it never showed.
        setError(
          isClientPlaceContext
            ? 'That did not place the tripwire. Check the email, password, and authenticator code, then try again.'
            : 'That sign-in didn’t match. Check the email, password, and authenticator code, then try again.',
        )
      } else if (isAxiosError(err) && err.response?.status === 422) {
        // Customer-facing copy: like the network case below, never mention
        // servers, backends, APIs, or request formats on the public door.
        setError(
          isClientPlaceContext
            ? 'That did not place the tripwire. Check the email, password, and authenticator code, then try again.'
            : 'That sign-in didn’t work. Check the email and authenticator code, then try again.',
        )
      } else if (isAxiosError(err) && !err.response) {
        // Customer-facing copy: never mention backends, APIs, or hosts here —
        // this door is public and the reader is a client, not a developer.
        setError('We could not reach DigitalWatch just now. Please try again.')
      } else {
        setError('Sign-in failed. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Password OK but TOTP not enrolled: fetch the setup material with the
  // grant, then transition to the one-time enrolment step. This is NOT a
  // session — no token is stored until verify-setup succeeds.
  const startEnrolment = async (token: string) => {
    const setupData = await totpSetup(token)
    setEnrolmentToken(token)
    setSetup(setupData)
    setEnrolCode('')
    setEnrolError(null)
    setStep('enrolment')
  }

  // BRIEF-B16: the chosen business's sign-in. The same email and password go
  // again with its business_id, and no authenticator code; the authenticator
  // code, texted code or enrolment that follow are that business's own.
  const handleChooseBusiness = async (businessId: string) => {
    if (!choiceLogin) return
    const login: HeldLogin = { ...choiceLogin, business_id: businessId }
    setChoiceError(null)
    setChoiceLoading(true)
    try {
      const result = await analystLogin(login)
      if (isAuthenticatorCodeRequired(result)) {
        setChoiceLogin(null)
        setChosenLogin(login)
        setAuthCode('')
        setAuthError(null)
        setStep('authenticator_code')
      } else if (isSignInCodeRequired(result)) {
        setChoiceLogin(null)
        setCodeLogin({ email: login.email, password: login.password, business_id: businessId })
        setTextedCode('')
        setTextedError(null)
        setStep('texted_code')
      } else if (isEnrolmentRequired(result)) {
        setChoiceLogin(null)
        await startEnrolment(result.enrolment_token)
      } else if (!isBusinessChoiceRequired(result)) {
        setChoiceLogin(null)
        await completeLogin(result.access_token, result.principal.role)
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.detail === NO_PHONE_MESSAGE) {
        setChoiceError(NO_PHONE_MESSAGE)
      } else if (isAxiosError(err) && err.response?.status === 401) {
        // That business would not open now (for example it has been
        // locked after too many tries): start the sign-in again.
        setChoiceLogin(null)
        setBusinesses([])
        setStep('credentials')
        setError('That sign-in didn’t match. Check the email and password, then try again.')
      } else if (isAxiosError(err) && err.response?.status === 429) {
        setChoiceError('Too many tries. Please wait a little and sign in again.')
      } else if (isAxiosError(err) && !err.response) {
        setChoiceError('We could not reach DigitalWatch just now. Please try again.')
      } else {
        setChoiceError('Sign-in failed. Please try again.')
      }
    } finally {
      setChoiceLoading(false)
    }
  }

  // BRIEF-B16: the chosen business's own authenticator code, after the choice.
  const handleAuthenticatorCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!chosenLogin) return
    const data = new FormData(e.currentTarget)
    const code = String(data.get('chosen-code') || authCode).replace(/\D/g, '')
    setAuthCode(code)
    if (!/^\d{6}$/.test(code)) {
      setAuthError('Enter the 6-digit code from your authenticator app.')
      return
    }
    setAuthError(null)
    setAuthLoading(true)
    try {
      const result = await analystLogin({ ...chosenLogin, totp_code: code })
      if (isEnrolmentRequired(result)) {
        setChosenLogin(null)
        await startEnrolment(result.enrolment_token)
      } else if (
        !isSignInCodeRequired(result) &&
        !isBusinessChoiceRequired(result) &&
        !isAuthenticatorCodeRequired(result)
      ) {
        setChosenLogin(null)
        await completeLogin(result.access_token, result.principal.role)
      } else {
        // An answer this step does not expect: say so rather than sit still.
        setAuthError('We could not finish signing you in. Please start again.')
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 401) {
        setAuthError('That code didn’t match. Check your authenticator app and try again.')
      } else if (isAxiosError(err) && err.response?.status === 429) {
        setAuthError('Too many tries. Please wait a little and sign in again.')
      } else if (isAxiosError(err) && !err.response) {
        setAuthError('We could not reach DigitalWatch just now. Please try again.')
      } else {
        setAuthError('We could not check that code just now. Please try again.')
      }
    } finally {
      setAuthLoading(false)
    }
  }

  const handleTextedCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const code = textedCode.replace(/\D/g, '')
    if (!codeLogin) return
    if (!/^\d{6}$/.test(code)) {
      setTextedError('Enter the 6-digit code from the text message.')
      return
    }
    setTextedError(null)
    setTextedLoading(true)
    try {
      const result = await analystLogin({ ...codeLogin, phone_code: code })
      if (isEnrolmentRequired(result)) {
        setCodeLogin(null)
        await startEnrolment(result.enrolment_token)
      } else if (
        !isSignInCodeRequired(result) &&
        !isBusinessChoiceRequired(result) &&
        !isAuthenticatorCodeRequired(result)
      ) {
        setCodeLogin(null)
        await completeLogin(result.access_token, result.principal.role)
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 401) {
        setTextedError('That code didn’t match. Check the text message and try again.')
      } else if (isAxiosError(err) && err.response?.status === 429) {
        setTextedError('Too many tries. Go back and sign in again for a new code.')
      } else {
        setTextedError('We could not check that code just now. Please try again.')
      }
    } finally {
      setTextedLoading(false)
    }
  }

  const handleVerify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // A painted code (Safari / Chrome / authenticator AutoFill) is the
    // source of truth and can be formatted ("123 456", 7 chars) — read
    // FormData off the submitting form and strip non-digits BEFORE
    // setEnrolError/setEnrolLoading, same as hydratePaintedCredentials does
    // for the credentials step. Autofill routinely paints #enrol-code's
    // visible .value without ever committing FormData (no input/change
    // event fired), so FormData stays checked first, falls back to the
    // named input's own .value when that FormData entry is empty, and
    // falls back to React state only when both are empty (a scripted
    // submit in tests that never touched the DOM).
    const data = new FormData(e.currentTarget)
    const paintedCode = String(
      data.get('one-time-code') || readPaintedValue(e.currentTarget, 'one-time-code') || enrolCode,
    ).replace(/\D/g, '')
    setEnrolCode(paintedCode)
    setEnrolError(null)

    if (!/^\d{6}$/.test(paintedCode)) {
      setEnrolError('Enter the 6-digit code from your authenticator app.')
      return
    }
    if (!enrolmentToken) {
      expireBackToCredentials()
      return
    }

    setEnrolLoading(true)
    try {
      const session = await totpVerifySetup(enrolmentToken, paintedCode)
      // Success: the grant is burned server-side and a real session issued.
      await completeLogin(session.access_token, session.principal.role)
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 401) {
        // Wrong code: the grant was only peeked, never burned — let them retry.
        setEnrolError('That code was not correct. Check your authenticator app and try again.')
      } else if (isAxiosError(err) && err.response?.status === 403) {
        // Expired or already-spent grant → start over from the password step.
        expireBackToCredentials()
      } else if (isAxiosError(err) && err.response?.status === 400) {
        setEnrolError('Setup could not be completed. Please sign in again.')
      } else if (isAxiosError(err) && !err.response) {
        // Same customer-calm wording as the credentials step — no dev jargon.
        setEnrolError('We could not reach DigitalWatch just now. Please try again.')
      } else {
        setEnrolError('Verification failed. Please try again.')
      }
    } finally {
      setEnrolLoading(false)
    }
  }

  const copySecret = async () => {
    if (!setup) return
    try {
      await navigator.clipboard.writeText(setup.secret)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard unavailable (insecure context / permissions): the secret is
      // shown in full on screen for manual entry, so this is non-fatal.
    }
  }

  // Session bounce in flight — same "Getting ready…" hold as ProtectedRoute.
  // Never show the password form to someone who may already have a session.
  if (bouncing) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-brass border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Getting ready…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-brass/10 flex items-center justify-center mb-3">
            <Shield className="w-8 h-8 text-brass" aria-hidden="true" />
          </div>
          <h1
            className="text-2xl font-serif tracking-wide"
            style={{ color: '#c49a5a' }}
          >
            DigitalWatch
          </h1>
          <p className="text-xs text-gray-500 mt-1">Security Operations Centre</p>
        </div>

        {/* Card */}
        <div className="bg-cream rounded-2xl p-6 shadow-xl">
          {step === 'credentials' ? (
            <>
              <h2 className="text-lg font-semibold text-navy mb-1">
                {isClientPlaceContext ? 'Set a tripwire' : 'Sign in to your security team'}
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                {isClientPlaceContext
                  ? 'Your password and authenticator code place the tripwire.'
                  : 'Your DigitalWatch password and authenticator code, separate from your work email sign-in.'}
              </p>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-danger-light text-danger text-sm"
                  role="alert"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  {error}
                </motion.div>
              )}

              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                // DW-CLIENT-PLACE-NATIVE-EMAIL-MUST-NOT-BLOCK: the place door's
                // #email keeps type="email" + required for the password manager
                // and assistive tech, but native HTML5 validity can silently
                // swallow the click before handleSubmit ever runs — a painted
                // address with a trailing space (iCloud Keychain) fails
                // type="email", or a paint that lands a tick after the click
                // fails required. JS below re-checks email/password non-empty
                // (and the existing 6-digit totp gate) with the same honest
                // place-error copy, so noValidate loses nothing here. The
                // ordinary "/" door has no such swallow risk worth losing
                // native validation for, so it keeps browser validation.
                noValidate={isClientPlaceContext}
              >
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-navy mb-1">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full tap-min px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                    // The place door signs the client into their OWN existing
                    // account (never a new one) purely to place a tripwire —
                    // autoComplete="email" tags this as a contact/signup
                    // field to a password manager, which then has no saved
                    // login to offer and leaves email+password unpainted.
                    // "username" is the field a password manager matches
                    // against a saved login for this site, same as the
                    // ordinary "/" door would want if it ever collected a
                    // non-email identifier — here it still holds the address
                    // itself, just labelled as the login identifier it is.
                    autoComplete={isClientPlaceContext ? 'username' : 'email'}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-navy mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      required
                      className="w-full tap-min px-3 py-2.5 pr-10 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const form = e.currentTarget.closest('form')
                        if (form) hydratePaintedCredentials(form)
                        setShowPassword((v) => !v)
                      }}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 tap-min p-1 text-gray-400 hover:text-gray-600"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" aria-hidden="true" />
                      ) : (
                        <Eye className="w-4 h-4" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="totp" className="block text-sm font-medium text-navy mb-1">
                    Authenticator code
                  </label>
                  {/* No pattern/maxLength: autofill can paint a formatted
                      code (e.g. "123 456") that's >6 chars pre-strip — a
                      native constraint here blocks handleSubmit before the
                      digit-strip below ever runs. */}
                  <input
                    id="totp"
                    name="one-time-code"
                    type="text"
                    inputMode="numeric"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit code"
                    required={isClientPlaceContext}
                    className="w-full tap-min px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent tracking-widest"
                    autoComplete={isClientPlaceContext ? 'off' : 'one-time-code'}
                  />
                  {!isClientPlaceContext && (
                    <p className="text-[11px] text-gray-400 mt-1">
                      From your authenticator app. If you have not set one up
                      yet, leave this blank — we will walk you through it
                      after your password.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full tap-min py-2.5 rounded-lg bg-brass text-white text-sm font-semibold hover:bg-brass-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {isClientPlaceContext ? 'Setting a tripwire...' : 'Signing in...'}
                    </span>
                  ) : isClientPlaceContext ? (
                    'Set a tripwire'
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              <button
                type="button"
                onClick={openFirstPassword}
                className="w-full tap-min mt-4 text-sm text-brass hover:text-brass-dark underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-brass rounded-lg"
              >
                {isClientPlaceContext
                  ? 'No password yet? Set one, then we place a tripwire.'
                  : 'No password yet? Set one up.'}
              </button>
            </>
          ) : step === 'first_password' ? (
            <>
              <h2 className="text-lg font-semibold text-navy mb-1">Set your first password</h2>

              {fpSent ? (
                <div className="space-y-4">
                  {/* Generic on purpose: never echo the typed address, never
                      confirm an account exists, never claim a meeting
                      happened — the backend answers every address the same. */}
                  <p className="text-sm text-navy mt-4" role="status">
                    If that address is one we know, we will email it a link to
                    set your password. The link will not appear on this
                    screen — open the email, choose a password there, then come
                    back and sign in{isClientPlaceContext ? ' here to place a tripwire' : ''}.
                    The first time you sign in, we text a code to the mobile
                    given at sign-up.
                  </p>
                  <button
                    type="button"
                    onClick={backToCredentials}
                    className="w-full tap-min py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-navy hover:border-brass transition-colors focus:outline-none focus:ring-2 focus:ring-brass"
                  >
                    {isClientPlaceContext ? 'Back to set a tripwire.' : 'Back to sign in'}
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-5">
                    Enter your email. If it is one we know, we will email you a
                    link to set your password.
                  </p>

                  {fpError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-danger-light text-danger text-sm"
                      role="alert"
                    >
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      {fpError}
                    </motion.div>
                  )}

                  <form onSubmit={handleForgot} className="space-y-4">
                    <div>
                      <label htmlFor="fp-email" className="block text-sm font-medium text-navy mb-1">
                        Email
                      </label>
                      <input
                        id="fp-email"
                        type="email"
                        value={fpEmail}
                        onChange={(e) => setFpEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        autoFocus
                        className="w-full tap-min px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                        autoComplete="email"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={fpLoading}
                      className="w-full tap-min py-2.5 rounded-lg bg-brass text-white text-sm font-semibold hover:bg-brass-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
                    >
                      {fpLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        'Email me a set-password link'
                      )}
                    </button>
                  </form>

                  <button
                    type="button"
                    onClick={backToCredentials}
                    className="w-full tap-min mt-4 text-sm text-gray-500 hover:text-navy underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-brass rounded-lg"
                  >
                    {isClientPlaceContext ? 'Back to set a tripwire.' : 'Back to sign in'}
                  </button>
                </>
              )}
            </>
          ) : step === 'business_choice' ? (
            <>
              <h2 className="text-lg font-semibold text-navy mb-1">
                Which business are you signing in to?
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                Your email belongs to more than one business. Choose one, then
                we&apos;ll ask for that business&apos;s own code.
              </p>

              {choiceError && (
                <div
                  className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-danger-light text-danger text-sm"
                  role="alert"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  {choiceError}
                </div>
              )}

              <ul className="space-y-2" aria-label="Your businesses">
                {businesses.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      disabled={choiceLoading}
                      onClick={() => void handleChooseBusiness(b.id)}
                      className="w-full tap-min py-2.5 px-3 rounded-lg border border-gray-300 bg-white text-left text-sm font-semibold text-navy hover:border-brass disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
                    >
                      {b.name}
                    </button>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => {
                  setChoiceLogin(null)
                  setBusinesses([])
                  backToCredentials()
                }}
                className="w-full tap-min mt-4 text-sm text-gray-500 hover:text-navy underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-brass rounded-lg"
              >
                Back to sign in
              </button>
            </>
          ) : step === 'authenticator_code' ? (
            <>
              <h2 className="text-lg font-semibold text-navy mb-1">
                Enter your authenticator code
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                Use the code your authenticator app shows for the business you
                chose.
              </p>

              {authError && (
                <div
                  className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-danger-light text-danger text-sm"
                  role="alert"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  {authError}
                </div>
              )}

              <form onSubmit={handleAuthenticatorCode} className="space-y-4">
                <div>
                  <label htmlFor="chosen-code" className="block text-sm font-medium text-navy mb-1">
                    Authenticator code
                  </label>
                  <input
                    id="chosen-code"
                    name="chosen-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={7}
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value)}
                    placeholder="000000"
                    required
                    autoFocus
                    className="w-full tap-min px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 tracking-widest focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full tap-min py-2.5 rounded-lg bg-brass text-white text-sm font-semibold hover:bg-brass-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
                >
                  {authLoading ? 'Checking...' : 'Sign in'}
                </button>
              </form>

              <button
                type="button"
                onClick={() => {
                  setChosenLogin(null)
                  setAuthCode('')
                  backToCredentials()
                }}
                className="w-full tap-min mt-4 text-sm text-gray-500 hover:text-navy underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-brass rounded-lg"
              >
                Back to sign in
              </button>
            </>
          ) : step === 'texted_code' ? (
            <>
              <h2 className="text-lg font-semibold text-navy mb-1">Enter the code we texted you</h2>
              <p className="text-sm text-gray-500 mb-5">
                We sent a 6-digit code to the mobile given at sign-up. Your
                email alone is not enough to sign in.
              </p>

              {textedError && (
                <div
                  className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-danger-light text-danger text-sm"
                  role="alert"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  {textedError}
                </div>
              )}

              <form onSubmit={handleTextedCode} className="space-y-4">
                <div>
                  <label htmlFor="texted-code" className="block text-sm font-medium text-navy mb-1">
                    Code from the text message
                  </label>
                  <input
                    id="texted-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={textedCode}
                    onChange={(e) => setTextedCode(e.target.value)}
                    placeholder="000000"
                    required
                    autoFocus
                    className="w-full tap-min px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 tracking-widest focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={textedLoading}
                  className="w-full tap-min py-2.5 rounded-lg bg-brass text-white text-sm font-semibold hover:bg-brass-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
                >
                  {textedLoading ? 'Checking...' : 'Continue'}
                </button>
              </form>

              <button
                type="button"
                onClick={() => {
                  setCodeLogin(null)
                  backToCredentials()
                }}
                className="w-full tap-min mt-4 text-sm text-gray-500 hover:text-navy underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-brass rounded-lg"
              >
                Back to sign in
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-5 h-5 text-brass" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-navy">
                  {isClientPlaceContext ? 'Then we place the tripwire' : 'Set up your authenticator'}
                </h2>
              </div>
              <p className="text-sm text-gray-500 mb-5">
                {isClientPlaceContext
                  ? 'One authenticator code, then we place it.'
                  : 'Your account needs an authenticator. This is a one-time setup.'}
              </p>

              {enrolError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-danger-light text-danger text-sm"
                  role="alert"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  {enrolError}
                </motion.div>
              )}

              {setup && (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-navy mb-2">
                      Add a new account in your authenticator app (Google
                      Authenticator, Authy, 1Password…) using this setup key:
                    </p>
                    <div className="flex items-stretch gap-2">
                      <code
                        className="flex-1 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy font-mono tracking-wider break-all select-all"
                        aria-label="TOTP setup key"
                      >
                        {setup.secret}
                      </code>
                      <button
                        type="button"
                        onClick={copySecret}
                        className="tap-min px-3 rounded-lg border border-gray-300 bg-white text-gray-500 hover:text-navy hover:border-brass transition-colors flex items-center justify-center"
                        aria-label="Copy setup key"
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-success" aria-hidden="true" />
                        ) : (
                          <Copy className="w-4 h-4" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1 break-all">
                      Or open this link on the device with your authenticator:{' '}
                      <span className="font-mono text-gray-500">{setup.qr_code_uri}</span>
                    </p>
                  </div>

                  {setup.backup_codes.length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-white/60 p-3">
                      <p className="text-xs font-semibold text-navy mb-1">
                        Backup codes — save these now
                      </p>
                      <p className="text-[11px] text-gray-400 mb-2">
                        Each works once if you lose your authenticator. They will
                        not be shown again.
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {setup.backup_codes.map((code) => (
                          <code key={code} className="text-xs text-navy font-mono tracking-wide">
                            {code}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}

                  <form
                    onSubmit={handleVerify}
                    className="space-y-4"
                    // Same swallow risk v155 already closed on the credentials
                    // form: a late paint into #enrol-code can still leave
                    // native `required` unsatisfied for a tick, silently
                    // eating the click before handleVerify ever runs. JS
                    // above re-checks the 6-digit gate by hand, so noValidate
                    // loses nothing here. The ordinary "/" enrolment form has
                    // no such place-errand time pressure, so it keeps
                    // browser validation.
                    noValidate={isClientPlaceContext}
                  >
                    <div>
                      <label htmlFor="enrol-code" className="block text-sm font-medium text-navy mb-1">
                        Enter the 6-digit code
                      </label>
                      {/* No pattern/maxLength: autofill can paint a formatted
                          code (e.g. "123 456") that's >6 chars pre-strip — a
                          native constraint here blocks handleVerify before the
                          digit-strip below ever runs. Cap in JS after strip. */}
                      <input
                        id="enrol-code"
                        name="one-time-code"
                        type="text"
                        inputMode="numeric"
                        value={enrolCode}
                        onChange={(e) => setEnrolCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="6-digit code"
                        required
                        autoFocus
                        className="w-full tap-min px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass focus:border-transparent tracking-widest"
                        autoComplete="one-time-code"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">
                        {isClientPlaceContext
                          ? 'Once it verifies, we place the tripwire.'
                          : 'Once it verifies, your authenticator is registered and you will be signed in.'}
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={enrolLoading}
                      className="w-full tap-min py-2.5 rounded-lg bg-brass text-white text-sm font-semibold hover:bg-brass-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
                    >
                      {enrolLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          {isClientPlaceContext ? 'Setting a tripwire...' : 'Verifying...'}
                        </span>
                      ) : isClientPlaceContext ? (
                        'Verify and set the tripwire'
                      ) : (
                        'Verify & finish setup'
                      )}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>

        {/* BRIEF-E5e: a new business can find sign-up from the door. */}
        <p className="text-center text-sm text-gray-400 mt-6">
          New to DigitalWatch?{' '}
          <Link
            to="/smb"
            className="text-brass-light font-medium underline underline-offset-2 hover:text-cream"
          >
            Start here
          </Link>
        </p>

        {/* Product name only — the public login door must not claim the
            visitor is protected/watched (response may be disarmed and no
            monitoring may exist yet for them). */}
        <p className="text-center text-xs text-gray-600 mt-4">
          DigitalWatch
        </p>
      </motion.div>
    </div>
  )
}
